import { computed, unref, type MaybeRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { OrganizationRights, Rights, PlatformRights } from '@/clients/v1';
import { ProjectType } from '@/clients/v2';

import { useOrganizationsStore } from '@/stores/v2/OrganizationStore.ts';
import { useProjectsStoreV2 } from '@/stores/v2/ProjectStore.ts';

import { pageInfoOutlined } from '@/assets/icons/materialSymbols/MaterialSymbols.ts';
import { hasOrganizationRights, hasPlatformRights, hasProjectRights } from '@/composables/Auth.ts';
import { isDev } from '@/config/env.ts';
import { useFlagsAccess } from '@/composables/Flags.ts';
import { defaultDrawerIds, organizationIds, policySidebarIds } from '@/utils/DataTestIds.ts';
import { matchesRoute, type NavigationType } from './utils/sidebarRoutes.ts';

export interface SidebarItem {
  name: string;
  label: string;
  to?: string | { name: string };
  href?: string;
  icon: string;
  activeRoutes?: string[];
  sections?: NavigationType[];
  showCondition: boolean;
  dataTestId?: string;
}

export function useSidebar(type?: MaybeRef<NavigationType>) {
  const { t } = useI18n();
  const route = useRoute();
  const projectsStore = useProjectsStoreV2();
  const organizationStore = useOrganizationsStore();

  const { haveUserAccess, haveProjectRisksAccess } = useFlagsAccess();

  const routeName = computed(() => route.name?.toString() ?? '');
  const routeSection = computed(() => route.meta.sidebarSection ?? null);
  const isItemActive = (item: SidebarItem): boolean => {
    const section = routeSection.value;
    if (section && item.sections?.includes(section)) return true;
    return item.activeRoutes ? matchesRoute(routeName.value, item.activeRoutes) : false;
  };

  const activeTitle = computed(() => {
    switch (routeSection.value) {
      case 'admin':
        return t('Admin');
      case 'ai-registry':
        return t('AI Registry');
      case 'organization':
        return t('Organization');
      case 'project':
        return projectsStore.project?.name ?? '';
      case 'settings':
        return t('Settings');
      case 'policies':
        return t('Policy Center');
      default:
        return '';
    }
  });

  const currentType = computed(() => (type ? unref(type) : routeSection.value));

  const isProjectRoute = computed(() => routeSection.value === 'project');
  const isProjectLoading = computed(() => isProjectRoute.value && !projectsStore.project);

  const sidebarTopItems = computed((): SidebarItem[] => [
    {
      name: 'projects',
      label: t('AI Registry'),
      to: { name: 'projects' },
      icon: 'o_folder_copy',
      sections: ['ai-registry', 'project'],
      dataTestId: defaultDrawerIds.drawerProjectsIcon,
      showCondition: true,
    },
    {
      name: 'account_tree',
      label: t('Organization'),
      to: '/organization',
      icon: 'o_account_tree',
      sections: ['organization'],
      dataTestId: defaultDrawerIds.drawerOrganizationIcon,
      showCondition: true,
    },
    {
      name: 'storefront',
      label: t('Vendors'),
      to: '/vendors',
      icon: 'o_storefront',
      activeRoutes: ['vendors', 'vendors-overview', 'vendor-detail'],
      dataTestId: defaultDrawerIds.drawerVendorsIcon,
      showCondition: organizationStore.governanceEnabled && organizationStore.vendorsEnabled,
    },
    {
      name: 'policies',
      label: t('Policy Center'),
      to: '/policies',
      icon: 'o_policy',
      sections: ['policies'],
      dataTestId: defaultDrawerIds.drawerPoliciesIcon,
      showCondition: hasOrganizationRights(OrganizationRights.OrganizationsPolicyRead),
    },

    {
      name: 'icon-support',
      label: t('Support'),
      href: isDev ? 'https://docs.dev.modulos.ai' : 'https://docs.modulos.ai',
      icon: 'o_help_outline',
      dataTestId: defaultDrawerIds.drawerSupportIcon,
      showCondition: true,
    },
  ]);

  const middleItemsConfigs = computed((): Record<NavigationType, SidebarItem[]> => ({
    'ai-registry': [
      {
        name: 'ai-systems',
        label: t('AI Systems'),
        to: { name: 'projects' },
        icon: 'o_list_alt',
        activeRoutes: ['projects', 'edit-ai-system', 'project-'],
        showCondition: true,
      },
      {
        name: 'my-submissions',
        label: t('My Submissions'),
        to: { name: 'my-submissions' },
        icon: 'o_assignment_ind',
        activeRoutes: ['my-submissions'],
        showCondition: true,
      },
    ],
    project: [
      {
        name: 'overview',
        label: t('Overview'),
        to: `/ai-registry/ai-systems/${projectsStore.project?.id}/overview`,
        icon: 'o_summarize',
        activeRoutes: ['project-overview'],
        showCondition: projectsStore.project?.type !== ProjectType.Organization,
      },
      {
        name: 'dashboard',
        label: t('Dashboard'),
        to: `/ai-registry/ai-systems/${projectsStore.project?.id}`,
        icon: 'o_dashboard',
        activeRoutes: ['project-dashboard'],
        dataTestId: defaultDrawerIds.drawerDashboardIcon,
        showCondition: true,
      },
      {
        name: 'requirements',
        label: t('Requirements'),
        to: { name: 'project-requirements' },
        icon: 'o_beenhere',
        activeRoutes: ['project-requirements', 'requirement-detail'],
        showCondition: organizationStore.governanceEnabled,
      },
      {
        name: 'controls',
        label: t('Controls'),
        to: { name: 'project-controls' },
        icon: pageInfoOutlined,
        activeRoutes: ['project-controls'],
        showCondition: organizationStore.governanceEnabled,
      },
      {
        name: 'graph',
        label: t('Graph'),
        to: { name: 'project-graph' },
        icon: 'o_schema',
        activeRoutes: ['project-graph'],
        showCondition: organizationStore.governanceEnabled,
      },
      {
        name: 'risks',
        label: t('Risks'),
        to: { name: 'project-risks' },
        icon: 'o_trending_up',
        activeRoutes: [
          'project-risks',
          'risk-detail',
          'risk-quantification',
          'threat-quantification-detail',
        ],
        showCondition: organizationStore.riskManagementEnabled && haveProjectRisksAccess.value,
      },
      {
        name: 'evidence',
        label: t('Evidence'),
        to: { name: 'project-evidence' },
        icon: 'o_file_present',
        activeRoutes: ['project-evidence'],
        showCondition: organizationStore.governanceEnabled,
      },
      {
        name: 'assets',
        label: t('Assets'),
        to: { name: 'project-assets' },
        icon: 'o_dataset',
        activeRoutes: ['project-assets', 'asset-detail'],
        showCondition: organizationStore.governanceEnabled,
      },
      {
        name: 'testing',
        label: t('Runtime Inspection'),
        to: { name: 'project-runtime-inspection' },
        icon: 'o_bar_chart',
        activeRoutes: ['project-runtime-inspection', 'test-detail', 'agent-test-detail'],
        showCondition: organizationStore.governanceEnabled,
      },
      {
        name: 'jobs',
        label: t('Jobs'),
        to: { name: 'project-jobs' },
        icon: 'o_schedule',
        activeRoutes: ['project-jobs', 'job-detail'],
        showCondition: true,
      },
      {
        name: 'settings',
        label: t('Settings'),
        to: { name: 'project-settings' },
        icon: 'o_settings',
        activeRoutes: ['project-settings'],
        showCondition: projectsStore.project?.id
          ? hasProjectRights(projectsStore.project.id, Rights.ProjectsUpdate) ||
            hasProjectRights(projectsStore.project.id, Rights.ProjectsDelete) ||
            hasProjectRights(projectsStore.project.id, Rights.ProjectsUserRolesAssign)
          : false,
      },
    ],
    organization: [
      {
        name: 'risks',
        label: t('Risk Management'),
        to: '/organization/risk-management',
        icon: 'o_trending_up',
        activeRoutes: [
          'organization-risk-management',
          'organization-default',
          'organization-taxonomy-category-detail',
          'organization-taxonomy-risk-detail',
          'organization-taxonomy-threat-detail',
        ],
        showCondition:
          organizationStore.riskManagementEnabled &&
          hasOrganizationRights(OrganizationRights.OrganizationsRiskRead),
      },
      {
        name: 'users',
        label: t('User Management'),
        to: '/organization/users',
        icon: 'o_people',
        activeRoutes: ['organization-users'],
        showCondition: haveUserAccess.value,
        dataTestId: organizationIds.organizationUserManagement,
      },
      {
        name: 'frameworks',
        label: t('Frameworks'),
        to: '/organization/frameworks',
        icon: 'o_shield',
        activeRoutes: ['organization-frameworks', 'organization-framework-detail'],
        showCondition: organizationStore.governanceEnabled,
      },

      {
        name: 'source-connectors',
        label: t('Source & Connectors'),
        to: '/organization/source-connectors',
        icon: 'o_storage',
        activeRoutes: ['organization-source-connectors'],
        showCondition: hasOrganizationRights(OrganizationRights.OrganizationsConfigUpdate),
      },
      {
        name: 'notifications',
        label: t('Notifications'),
        to: '/organization/notifications',
        icon: 'o_notifications',
        activeRoutes: ['organization-notifications'],
        showCondition: hasOrganizationRights(OrganizationRights.OrganizationsConfigRead),
      },
      {
        name: 'usage',
        label: t('Usage'),
        to: '/organization/usage',
        icon: 'o_data_usage',
        activeRoutes: ['organization-usage'],
        showCondition: true,
        dataTestId: organizationIds.organizationUsage,
      },
      {
        name: 'settings',
        label: t('Settings'),
        to: '/organization/settings',
        icon: 'o_settings',
        activeRoutes: ['organization-settings'],
        showCondition: hasOrganizationRights(OrganizationRights.OrganizationsConfigRead),
      },
    ],
    admin: [
      {
        name: 'ai-usage',
        label: t('AI Usage'),
        to: '/admin/ai-usage',
        icon: 'o_monitoring',
        activeRoutes: [
          'admin-ai-usage',
          'admin-ai-usage-events',
          'admin-ai-usage-organization-detail',
        ],
        showCondition: hasPlatformRights(PlatformRights.PlatformOrganizationsMetricsRead),
      },
      {
        name: 'ai-pricing',
        label: t('AI Pricing'),
        to: '/admin/ai-pricing',
        icon: 'o_sell',
        activeRoutes: ['admin-ai-pricing'],
        showCondition: hasPlatformRights(PlatformRights.PlatformOrganizationsMetricsRead),
      },
      {
        name: 'organizations',
        label: t('Organizations'),
        to: '/admin/organizations',
        icon: 'o_account_tree',
        activeRoutes: ['admin', 'admin-organizations', 'admin-organization-detail'],
        showCondition: true,
      },
      {
        name: 'frameworks',
        label: t('Frameworks'),
        to: '/admin/frameworks',
        icon: 'o_shield',
        activeRoutes: [
          'admin-frameworks',
          'admin-frameworks-overview',
          'admin-framework-detail',
          'admin-new-template',
        ],
        showCondition: hasPlatformRights(PlatformRights.PlatformFrameworksRead),
      },
      {
        name: 'templates',
        label: t('Policy Templates'),
        to: '/admin/templates',
        icon: 'o_description',
        activeRoutes: [
          'admin-templates',
          'admin-templates-overview',
          'admin-template-detail',
          'admin-template-version-detail',
        ],
        showCondition: hasPlatformRights(PlatformRights.PlatformPolicyTemplateRead),
      },
    ],

    settings: [
      {
        name: 'user-settings',
        label: t('User Settings'),
        to: '/settings/user-settings',
        icon: 'o_person',
        activeRoutes: ['settings-default', 'user-settings'],
        showCondition: true,
      },
      {
        name: 'notifications',
        label: t('Notifications'),
        to: '/settings/notifications',
        icon: 'o_notifications',
        activeRoutes: ['settings-notifications'],
        showCondition: true,
      },
    ],
    policies: [
      {
        name: 'my-policies',
        label: t('My Tasks'),
        to: { name: 'policies-my-policies' },
        icon: 'o_description',
        activeRoutes: ['policies-my-policies', 'my-policy-version-detail'],
        showCondition: hasOrganizationRights(OrganizationRights.OrganizationsPolicyRead),
        dataTestId: policySidebarIds.sidebarMyTasks,
      },
      {
        name: 'policy-management',
        label: t('Policy Management'),
        to: { name: 'policies-management' },
        icon: 'o_admin_panel_settings',
        activeRoutes: [
          'policies-management',
          'management-policy-detail',
          'management-policy-version-detail',
        ],
        showCondition: hasOrganizationRights(OrganizationRights.OrganizationsPolicyEdit),
        dataTestId: policySidebarIds.sidebarPolicyManagement,
      },
      {
        name: 'templates-catalog',
        label: t('Templates Catalog'),
        to: { name: 'policies-templates-catalog' },
        icon: 'o_description',
        activeRoutes: ['policies-templates-catalog'],
        showCondition: hasOrganizationRights(OrganizationRights.OrganizationsPolicyEdit),
        dataTestId: policySidebarIds.sidebarTemplatesCatalog,
      },
      {
        name: 'acknowledgments',
        label: t('Acknowledgements'),
        to: { name: 'policies-acknowledgments' },
        icon: 'o_fact_check',
        activeRoutes: ['policies-acknowledgments'],
        showCondition: hasOrganizationRights(OrganizationRights.OrganizationsPolicyEdit),
        dataTestId: policySidebarIds.sidebarAcknowledgements,
      },
    ],
  }));

  const middleItems = computed(() =>
    currentType.value ? middleItemsConfigs.value[currentType.value] : [],
  );

  const sidebarMiddleItems = computed(() =>
    middleItems.value.filter((item) => item.showCondition !== false),
  );

  return {
    route,
    sidebarTopItems,
    sidebarMiddleItems,
    activeTitle,
    isProjectRoute,
    isProjectLoading,
    isItemActive,
  };
}
